import React, { useRef, useEffect, useState } from 'react';
import GpayScratchFoil, {
  isCouponScratched,
  loadScratchedIds,
} from './GpayScratchFoil';
import './CouponReelsModal.css';

const CouponReelsModal = ({
  coupons,
  initialIndex = 0,
  isOpen = false,
  onClose,
  onClaim,
  onUtilizeLater,
  formatCurrency,
  getCouponAmount,
  getDayWisePricing,
  phone,
  customerName,
  navigate,
  /** 'redeem' = citizen wallet → shop order; 'claimBill' = AA staff smartbill path */
  primaryAction = 'claimBill',
}) => {
  const containerRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [revealedCoupons, setRevealedCoupons] = useState(() => loadScratchedIds());

  // Get color for day based on position (green to red gradient)
  const getDayColor = (index, total) => {
    if (total === 1) {
      return '#4caf50'; // Green
    }
    const ratio = index / (total - 1);
    // Green (0, 255, 0) to Red (255, 0, 0)
    const r = Math.round(255 * ratio);
    const g = Math.round(255 * (1 - ratio));
    const b = 0;
    return `rgb(${r}, ${g}, ${b})`;
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setCurrentIndex(initialIndex);
      // Scroll to initial coupon - ensure it's properly centered
      setTimeout(() => {
        if (containerRef.current) {
          const couponElement = containerRef.current.querySelector(`[data-coupon-index="${initialIndex}"]`);
          if (couponElement) {
            couponElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Also ensure container is at the right position
            containerRef.current.scrollTop = couponElement.offsetTop - (containerRef.current.clientHeight / 2) + (couponElement.clientHeight / 2);
          }
        }
      }, 150);
    } else {
      document.body.style.overflow = '';
    }

    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, initialIndex, onClose]);

  // Handle scroll to detect current coupon
  useEffect(() => {
    if (!isOpen || !containerRef.current) return;

    let scrollTimeout;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const container = containerRef.current;
        if (!container) return;
        
        const coupons = container.querySelectorAll('.coupon-reel-item');
        const containerRect = container.getBoundingClientRect();
        const containerCenter = containerRect.top + containerRect.height / 2;

        let closestIndex = 0;
        let closestDistance = Infinity;

        coupons.forEach((coupon, index) => {
          const couponRect = coupon.getBoundingClientRect();
          const couponCenter = couponRect.top + couponRect.height / 2;
          const distance = Math.abs(couponCenter - containerCenter);
          
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        if (closestIndex !== currentIndex) {
          setCurrentIndex(closestIndex);
        }
      }, 50);
    };

    const container = containerRef.current;
    container.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial check

    return () => {
      clearTimeout(scrollTimeout);
      container.removeEventListener('scroll', handleScroll);
    };
  }, [isOpen, coupons.length, currentIndex]);

  const handleReveal = (couponId) => {
    const id = String(couponId);
    if (revealedCoupons.has(id)) return;
    setRevealedCoupons((prev) => new Set([...prev, id]));
  };

  const touchStartY = useRef(0);
  const touchEndY = useRef(0);

  const handleWheel = (e) => {
    if (!containerRef.current) return;
    
    const delta = e.deltaY;
    const container = containerRef.current;
    const coupons = container.querySelectorAll('.coupon-reel-item');
    
    if (Math.abs(delta) > 10) {
      if (delta > 0 && currentIndex < coupons.length - 1) {
        // Scroll down
        const nextCoupon = coupons[currentIndex + 1];
        if (nextCoupon) {
          e.preventDefault();
          nextCoupon.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } else if (delta < 0 && currentIndex > 0) {
        // Scroll up
        const prevCoupon = coupons[currentIndex - 1];
        if (prevCoupon) {
          e.preventDefault();
          prevCoupon.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }
  };

  const handleTouchStart = (e) => {
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e) => {
    touchEndY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = () => {
    if (!containerRef.current) return;
    
    const swipeDistance = touchStartY.current - touchEndY.current;
    const minSwipeDistance = 50;
    const coupons = containerRef.current.querySelectorAll('.coupon-reel-item');
    
    if (Math.abs(swipeDistance) > minSwipeDistance) {
      if (swipeDistance > 0 && currentIndex < coupons.length - 1) {
        // Swipe up - next coupon
        const nextCoupon = coupons[currentIndex + 1];
        if (nextCoupon) {
          nextCoupon.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } else if (swipeDistance < 0 && currentIndex > 0) {
        // Swipe down - previous coupon
        const prevCoupon = coupons[currentIndex - 1];
        if (prevCoupon) {
          prevCoupon.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }
  };

  if (!isOpen || coupons.length === 0) return null;

  return (
    <div className="coupon-reels-modal-overlay" onClick={onClose}>
      <div 
        className="coupon-reels-modal-container" 
        ref={containerRef}
        onWheel={handleWheel}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="coupon-reels-close" onClick={onClose}>
          <i className="fas fa-times"></i>
        </button>
        
        <div className="coupon-reels-counter">
          {currentIndex + 1} / {coupons.length}
        </div>

        {coupons.map((coupon, index) => {
          const originalAmount = Number(coupon.actual_price || coupon.original_price || 0);
          const offerAmount = getCouponAmount(coupon);
          const discountAmount = Math.max(originalAmount - offerAmount, 0);
          const isUsed = coupon.status === 'used';
          const validUntil = coupon.valid_until || coupon.validUntil;
          const isExpired = validUntil ? new Date(validUntil) < new Date() : false;
          const alreadyStored = isCouponScratched(coupon.id) || !!coupon.scratched_at;
          const isRevealed =
            revealedCoupons.has(String(coupon.id)) ||
            alreadyStored ||
            isUsed ||
            isExpired;
          const discountLabel =
            discountAmount > 0
              ? `₹${Math.round(discountAmount)} OFF`
              : offerAmount > 0
                ? formatCurrency(offerAmount)
                : 'REWARD';
          const code = coupon.coupon_code || coupon.code || '';
          const title = coupon.name || coupon.coupon_name || 'Coupon';
          const service = coupon.service_name || coupon.service || '';

          return (
            <div
              key={`${coupon.id}-${index}`}
              data-coupon-index={index}
              className={`coupon-reel-item gpay-reel-item ${index === currentIndex ? 'active' : ''}`}
              style={{
                scrollSnapAlign: 'center',
                scrollSnapStop: 'always',
              }}
            >
              <div className="coupon-reel-content gpay-reel-content">
                <div className="gpay-reel-top">
                  <h3>{isRevealed ? 'Your reward' : 'Scratch to unlock'}</h3>
                  <span className={`coupon-status ${isUsed ? 'used' : isExpired ? 'expired' : 'active'}`}>
                    {isUsed ? 'Used' : isExpired ? 'Expired' : isRevealed ? 'Ready' : 'New'}
                  </span>
                </div>

                <GpayScratchFoil
                  couponId={coupon.id}
                  alreadyRevealed={isRevealed}
                  discountLabel={discountLabel}
                  height={260}
                  onRevealed={() => handleReveal(coupon.id)}
                >
                  <div className="gpay-reward-meta">
                    <span className="gpay-reward-badge">YOU WON</span>
                    {(coupon.mitra_name || coupon.shop_name || service) ? (
                      <span className="gpay-reward-shop">
                        {coupon.mitra_name || coupon.shop_name || service}
                      </span>
                    ) : null}
                  </div>
                  <div className="gpay-reward-discount">{discountLabel}</div>
                  <p className="gpay-reward-title">{title}</p>
                  {service ? <p className="gpay-reward-sub">{service}</p> : null}
                  {isRevealed && originalAmount > 0 ? (
                    <p className="gpay-reward-sub">
                      Was {formatCurrency(originalAmount)} · Now {formatCurrency(offerAmount)}
                    </p>
                  ) : null}
                  {isRevealed && code ? (
                    <div className="gpay-reward-code">{code}</div>
                  ) : null}
                </GpayScratchFoil>

                {isRevealed && (
                  <div className="coupon-reel-body gpay-reel-details">
                    <div className="coupon-dates-reel">
                      {(coupon.mitra_name || coupon.shop_name) && (
                        <div className="coupon-initiated-reel">
                          <i className="fas fa-store"></i> Shop:{' '}
                          {coupon.mitra_name || coupon.shop_name}
                        </div>
                      )}
                      {(coupon.generated_at || coupon.issued_at || coupon.issued_date || coupon.created_at) && (
                        <div className="coupon-initiated-reel">
                          <i className="fas fa-clock"></i> Issued:{' '}
                          {new Date(coupon.generated_at || coupon.issued_at || coupon.issued_date || coupon.created_at).toLocaleString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                          })}
                        </div>
                      )}
                      {coupon.scratched_at && (
                        <div className="coupon-initiated-reel">
                          <i className="fas fa-hand-pointer"></i> Scratched:{' '}
                          {new Date(coupon.scratched_at).toLocaleString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                          })}
                        </div>
                      )}
                      {coupon.valid_until && (
                        <div className="coupon-validity-reel">
                          <i className="fas fa-calendar"></i> Valid until:{' '}
                          {new Date(coupon.valid_until).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </div>
                      )}
                    </div>
                    {getDayWisePricing && (() => {
                      const dayWisePricing = getDayWisePricing(coupon);
                      if (dayWisePricing && dayWisePricing.pricingDays && dayWisePricing.pricingDays.length > 0) {
                        return (
                          <div className="day-wise-pricing-reel">
                            <div className="day-wise-pricing-title-reel">
                              <i className="fas fa-calendar-alt"></i> Day-wise Offer Prices
                            </div>
                            <div className="day-wise-pricing-grid-reel">
                              {dayWisePricing.pricingDays.map((dayInfo, idx) => {
                                const totalDays = dayWisePricing.pricingDays.length;
                                const color = getDayColor(idx, totalDays);
                                return (
                                  <div 
                                    key={dayInfo.day} 
                                    className={`day-price-item-reel ${dayInfo.isToday ? 'today-reel' : ''} ${dayInfo.isPast ? 'past-reel' : ''}`}
                                    style={{ borderLeftColor: color }}
                                  >
                                    <div className="day-price-day-reel">
                                      Day {dayInfo.day}
                                    </div>
                                    <div className="day-price-date-reel">
                                      {dayInfo.date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                                    </div>
                                    <div className="day-price-amount-reel" style={{ color: color }}>
                                      {formatCurrency(dayInfo.price)}
                                    </div>
                                    {dayInfo.isToday && (
                                      <div className="day-price-badge-reel">Today</div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                )}

                {isRevealed && !isUsed && !isExpired && (
                  <div className="coupon-reel-actions">
                    <button
                      className="btn-claim-reel"
                      onClick={() => {
                        if (primaryAction === 'redeem') {
                          if (typeof onClaim === 'function') onClaim(coupon);
                          else onClose();
                          return;
                        }
                        // AA staff / customer dashboard: Claim & Create Bill
                        onClose();
                        if (typeof onClaim === 'function') onClaim(coupon);
                        if (typeof navigate === 'function') {
                          navigate(
                            `/bills/post-bill-coupons?phone=${phone}&name=${encodeURIComponent(
                              customerName || '',
                            )}`,
                          );
                        }
                      }}
                    >
                      <i className="fas fa-check-circle"></i>
                      {primaryAction === 'redeem'
                        ? 'Redeem → order'
                        : 'Claim & Create Bill'}
                    </button>
                    <button className="btn-utilize-later-reel" onClick={() => {
                      onUtilizeLater();
                    }}>
                      <i className="fas fa-clock"></i>
                      Utilize Later
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CouponReelsModal;

